import { Link, useNavigate } from '@tanstack/react-router'
import {
  Calendar,
  Check,
  Clock,
  Copy,
  Edit,
  Lock,
  MapPin,
  Share2,
  Star,
  Trash2,
  UsersRound,
  X,
} from 'lucide-react'
import { memo } from 'react'
import { OpenRequestsBadge } from '~/features/events/components/open-requests-badge'
import { formatEventDay } from '~/features/events/lib/event-date-format'
import { CardActionsMenu } from '~/features/shared/components/card-actions-menu'
import * as m from '~/paraglide/messages'
import { countryDisplayName } from '~/shared/lib/countries'
import { richTextToPlainText } from '~/shared/lib/rich-text'
import type { Event } from '~/shared/types'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shared/ui/tooltip'

type EventCardVariant = 'upcoming' | 'past'

type RenderedEventCardProps = {
  event: Event
  variant: EventCardVariant
  now: Date
  isPendingDelete: boolean
  isPendingPublish: boolean
  isPendingCancel: boolean
  onDelete: (id: string, title: string) => void
  onPublish: (id: string, title: string, hasCoverImage: boolean) => void
  onCancel: (id: string, title: string) => void
  onCopy: (id: string) => void
  onShare: (id: string) => void
}

export const RenderedEventCard = memo(function RenderedEventCard({
  event,
  variant,
  now,
  isPendingDelete,
  isPendingPublish,
  isPendingCancel,
  onDelete,
  onPublish,
  onCancel,
  onCopy,
  onShare,
}: RenderedEventCardProps) {
  const navigate = useNavigate()
  const isPast = variant === 'past'
  const isDraft = event.isDraft || event.status === 'Draft'
  const isLive =
    !event.isCanceled &&
    (event.status === 'Published' || event.status === 'Unlisted') &&
    new Date(event.startTime) <= now &&
    new Date(event.endTime) >= now

  return (
    <Card className="@container/card beacon-interactive group relative overflow-hidden flex flex-col">
      <Link
        to="/dashboard/events/$eventId"
        params={{ eventId: event.id }}
        aria-label={event.title}
        className="absolute inset-0 z-10"
      />
      <div className="absolute top-2 right-2 z-20">
        <CardActionsMenu
          label={m.cardOptions()}
          actions={[
            {
              label: m.edit(),
              icon: Edit,
              onSelect: () =>
                navigate({
                  to: '/dashboard/events/$eventId/edit',
                  params: { eventId: event.id },
                }),
            },
            { label: m.copy(), icon: Copy, onSelect: () => onCopy(event.id) },
            {
              // A disabled entry carries no tooltip, so the label states the condition.
              label: isDraft ? m.shareNeedsPublish() : m.share(),
              icon: Share2,
              onSelect: () => onShare(event.id),
              disabled: isDraft,
            },
            {
              label: m.delete(),
              icon: Trash2,
              onSelect: () => onDelete(event.id, event.title),
              disabled: isPendingDelete,
              destructive: true,
            },
          ]}
        />
      </div>
      {event.coverImageUrl ? (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={event.coverImageUrl}
            alt={event.title}
            className={`h-full w-full object-cover transition-all duration-300 ${isPast ? 'grayscale brightness-75' : ''}`}
          />
        </div>
      ) : (
        <div className="aspect-video w-full bg-muted/50 flex items-center justify-center">
          <Calendar className="h-12 w-12 text-muted-foreground/40" />
        </div>
      )}

      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="line-clamp-2 min-w-0 text-brand-subtle-foreground">
            {event.title}
          </CardTitle>
          <div className="flex flex-wrap gap-1 justify-end max-w-[50%]">
            {event.roomVisibility === 'request' && !isPast && (
              <OpenRequestsBadge eventId={event.id} />
            )}
            {event.roomVisibility === 'request' && (
              <Badge variant="outline" className="gap-1 whitespace-nowrap">
                <Lock className="size-3" />
                {m.eventVisibilityRequest()}
              </Badge>
            )}
            {isDraft && (
              <Badge variant="secondary" className="whitespace-nowrap">
                {m.draft()}
              </Badge>
            )}
            {isLive && (
              <Badge className="whitespace-nowrap border border-green-500 bg-transparent text-green-600 hover:bg-transparent dark:border-green-400 dark:text-green-400 dark:hover:bg-transparent">
                {m.live()}
              </Badge>
            )}
            {event.membersOnly ? (
              <Badge variant="outline" className="gap-1 whitespace-nowrap">
                <UsersRound className="size-3" />
                {m.eventVisibilityMembers()}
              </Badge>
            ) : (
              event.status === 'Unlisted' && (
                <Badge variant="outline" className="whitespace-nowrap">
                  {m.unlisted()}
                </Badge>
              )
            )}
            {event.isCanceled && (
              <Badge className="whitespace-nowrap border border-red-400 bg-transparent text-red-500 hover:bg-transparent dark:border-red-500 dark:text-red-400 dark:hover:bg-transparent">
                {m.canceled()}
              </Badge>
            )}
          </div>
        </div>
        <CardDescription className="line-clamp-2 overflow-hidden wrap-break-word">
          {richTextToPlainText(event.description)}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <div className="space-y-3 flex-1">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>{formatEventDay(new Date(event.startTime))}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="line-clamp-1">
                {event.location.city}, {countryDisplayName(event.location.country)}
              </span>
            </div>
            <div
              className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground"
              title={m.likes()}
            >
              <Star className="h-4 w-4" fill="currentColor" aria-hidden="true" />
              <span className="tabular-nums">{event.interestedCount.toLocaleString()}</span>
              <span className="sr-only">{m.likes()}</span>
            </div>
          </div>
          <div className="text-sm font-medium">
            {event.price.kind === 'unknown' && m.priceUnknown()}
            {event.price.kind === 'free' && m.free()}
            {event.price.kind === 'fixed' && `€${event.price.priceFixed}`}
            {event.price.kind === 'range' &&
              `€${event.price.priceRangeMin} - €${event.price.priceRangeMax}`}
          </div>
        </div>

        {!isPast && (
          <div className="relative z-20 space-y-2 pt-4 mt-auto">
            {isDraft &&
              (event.coverImageUrl ? (
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  onClick={() => onPublish(event.id, event.title, !!event.coverImageUrl)}
                  disabled={isPendingPublish}
                >
                  <Check className="mr-2 h-4 w-4" />
                  {m.publish()}
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/dashboard/events/$eventId/edit"
                        params={{ eventId: event.id }}
                        search={{ scrollTo: 'images' }}
                        className="block"
                      >
                        <Button variant="default" size="sm" className="w-full" disabled>
                          <Check className="mr-2 h-4 w-4" />
                          {m.publish()}
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{m.addACoverImageToPublish1()}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}

            {!isDraft && !event.isCanceled && (
              <Button
                variant="outline"
                size="sm"
                className="w-full border-red-400 text-red-500 hover:bg-red-50 hover:text-red-600 dark:border-red-500 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
                onClick={() => onCancel(event.id, event.title)}
                disabled={isPendingCancel}
              >
                <X className="mr-2 h-4 w-4" />
                {m.cancelEvent()}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
})
